/**
 * navIcons.js — icon + colour metadata for every page_id in nav_manifest.json.
 *
 * The manifest owns page IDENTITY (page_id, label, tier, parent) and is shared
 * with the backend. Icons can't live in JSON, so they live here, keyed by the
 * same page_id. nav.js merges the two to build the rendered sidebar tree.
 */
import {
  Scale,
  ShoppingCart, Truck, MessageSquare, Kanban, Bug, BookOpen, FlaskConical,
  Bot, Settings2,
  Plus, GitBranch, Sprout, UserCheck, Workflow, MapPin, Target, Heart, Repeat,
  TrendingUp, Contact,
  LayoutDashboard, AlertTriangle, Activity, RotateCcw, Sparkles,
  PhoneCall, MessageCircle, Mail, ClipboardList, Star, Globe, MoreHorizontal,
  Inbox, Users, Eye, Network, ScrollText,
  Package, ShoppingBag, UserRound, Factory,
  Library, Zap,
  Webhook, Palette, Shield, Database, BellRing, Paintbrush, FileText, SlidersHorizontal, BadgePercent,
  Gauge, CalendarClock,
} from "lucide-react";

// page_id → { icon, color }
export const NAV_ICONS = {
  // ── Tier-1 sections ──
  "sales-crm":        { icon: ShoppingCart,  color: "text-orange-300" },
  "orders-logistics": { icon: Truck,         color: "text-emerald-300" },
  "comms":            { icon: MessageSquare, color: "text-sky-300" },
  "workspace":        { icon: Kanban,        color: "text-amber-300" },
  "dev-tracker":      { icon: Bug,           color: "text-pink-400" },
  "hub":              { icon: BookOpen,      color: "text-teal-300" },
  "lab":              { icon: FlaskConical,  color: "text-teal-300" },
  "lara":             { icon: Bot,           color: "text-purple-300" },
  "config":           { icon: Settings2,     color: "text-slate-300" },

  // ── Sales & CRM ──
  "/quotes":                  { icon: FileText,   color: "text-orange-300" },
  "/quotes/new":              { icon: Plus,       color: "text-orange-400" },
  "/deals":                   { icon: GitBranch,  color: "text-purple-300" },
  "/leads":                   { icon: Sprout,     color: "text-sky-300" },
  "/crm/customers":           { icon: UserCheck,  color: "text-emerald-300" },
  "/crm/velocity":            { icon: Gauge,      color: "text-lime-300" },
  "/lara/pipeline-workflows": { icon: Workflow,   color: "text-orange-300" },
  "/crm/trade-map":           { icon: MapPin,     color: "text-teal-300" },
  "/crm/leads":               { icon: Target,     color: "text-orange-300" },
  "/crm/ltv":                 { icon: Heart,      color: "text-pink-300" },
  "/crm/am-ai":               { icon: Bot,        color: "text-fuchsia-300" },
  "/crm/churn":               { icon: Repeat,     color: "text-rose-300" },
  "/sales/forecast":          { icon: TrendingUp, color: "text-fuchsia-300" },
  "/crm/misc":                { icon: Contact,    color: "text-slate-400" },

  // ── Orders & Logistics ──
  "/orders/dashboard": { icon: LayoutDashboard, color: "text-emerald-300" },
  "/orders/parked":    { icon: AlertTriangle,   color: "text-amber-300" },
  "/orders/feed":      { icon: Activity,        color: "text-teal-300" },
  "/orders/shipping":  { icon: Truck,           color: "text-sky-300" },
  "/orders/returns":   { icon: RotateCcw,       color: "text-orange-300" },

  // ── Communications ──
  "/calls":                { icon: PhoneCall,      color: "text-sky-300" },
  "/comms/whatsapp":       { icon: MessageCircle,  color: "text-emerald-300" },
  "/comms/email":          { icon: Mail,           color: "text-orange-300" },
  "/comms/forms":          { icon: ClipboardList,  color: "text-purple-300" },
  "/comms/trustpilot":     { icon: Star,           color: "text-emerald-400" },
  "/comms/google-reviews": { icon: Globe,          color: "text-amber-300" },
  "/comms/other":          { icon: MoreHorizontal, color: "text-slate-400" },

  // ── Workspace ──
  "/workspace/home":       { icon: LayoutDashboard, color: "text-amber-300" },
  "/workspace/my-tasks":   { icon: Inbox,           color: "text-sky-300" },
  "/workspace/teams":      { icon: Users,           color: "text-teal-300" },
  "/workspace/overwatch":  { icon: Eye,             color: "text-fuchsia-300" },
  "/workspace/org-chart":  { icon: Network,         color: "text-emerald-300" },
  "/workspace/full-squad": { icon: Users,           color: "text-emerald-200" },
  "/workspace/audit":      { icon: ScrollText,      color: "text-rose-300" },

  // ── Management ──
  "management":              { icon: Gauge,         color: "text-amber-300" },
  "/management/tasks":       { icon: Eye,           color: "text-fuchsia-300" },
  "/management/productivity": { icon: Activity,     color: "text-teal-300" },
  "/management/sales-sla":   { icon: Gauge,         color: "text-teal-300" },
  "/management/activities":  { icon: CalendarClock, color: "text-amber-300" },
  "/management/account-managers": { icon: CalendarClock, color: "text-fuchsia-300" },
  "/management/am-management": { icon: Scale, color: "text-teal-300" },

  // ── Dev Tracker / Hub (standalone links) ──
  "/items/all": { icon: Bug,      color: "text-pink-400" },
  "/hub":       { icon: BookOpen, color: "text-teal-300" },

  // ── Data Lab ──
  "/lab/health":    { icon: Activity,    color: "text-[#C8A46B]" },
  "/lab/enrichment": { icon: Sparkles,   color: "text-violet-300" },
  "/lab/products":  { icon: Package,    color: "text-teal-300" },
  "/lab/orders":    { icon: ShoppingBag,color: "text-orange-300" },
  "/lab/persons":   { icon: UserRound,  color: "text-pink-300" },
  "/lab/calls":     { icon: PhoneCall,  color: "text-sky-300" },
  "/lab/suppliers": { icon: Factory,    color: "text-amber-300" },
  "/lab/workflows": { icon: Workflow,   color: "text-emerald-300" },

  // ── Lara AI ──
  "/lara/library":   { icon: Library,      color: "text-fuchsia-300" },
  "/lara/knowledge": { icon: BookOpen,     color: "text-emerald-300" },
  "/lara/ai-config": { icon: SlidersHorizontal, color: "text-purple-300" },
  "/lara/hyperagent":{ icon: Zap,          color: "text-purple-300" },
  "/lara/workflows": { icon: Workflow,     color: "text-teal-300" },
  "/lara/audit":     { icon: ClipboardList,color: "text-slate-300" },

  // ── Config ──
  "/workspace/admin":      { icon: Kanban,    color: "text-amber-300" },
  "/admin/users":          { icon: Users,     color: "text-indigo-300" },
  "/admin/webhook-log":    { icon: Webhook,   color: "text-amber-300" },
  "/settings/backgrounds": { icon: Palette,   color: "text-teal-300" },
  "/config/roles":         { icon: Shield,    color: "text-rose-300" },
  "/config/data-sources":  { icon: Database,  color: "text-teal-300" },
  "/config/notifications": { icon: BellRing,  color: "text-amber-300" },
  "/config/branding":      { icon: Paintbrush,color: "text-fuchsia-300" },
  "/config/quote-pdf":     { icon: FileText,  color: "text-orange-300" },
  "/config/pipeline-automation": { icon: SlidersHorizontal, color: "text-amber-300" },
  "/config/account-manager-sync": { icon: SlidersHorizontal, color: "text-amber-300" },
  "/config/trade-discounts": { icon: BadgePercent, color: "text-orange-300" },
};

const FALLBACK = { icon: MoreHorizontal, color: "text-slate-400" };
export function iconFor(pageId) {
  return NAV_ICONS[pageId] || FALLBACK;
}
